import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import translations synchronously to prevent showing translation keys
import enTranslations from '../translations/en.json';
import srTranslations from '../translations/sr.json';
import esTranslations from '../translations/es.json';
import deTranslations from '../translations/de.json';

export type Language = 'en' | 'sr' | 'es' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tList: <T>(key: string) => T[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Pre-loaded translations to avoid async loading delay
const translations = {
  en: enTranslations,
  sr: srTranslations,
  es: esTranslations,
  de: deTranslations,
};

const LANGUAGE_STORAGE_KEY = 'juniorfk.language';

function readStoredLanguage(): Language {
  try {
    const s = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (s === 'en' || s === 'sr' || s === 'es' || s === 'de') return s;
  } catch {
    /* ignore */
  }
  return 'sr';
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  };

  const t = (key: string): string => {
    const currentTranslations = translations[language];
    if (!currentTranslations) return key;
    
    const keys = key.split('.');
    let translation: any = currentTranslations;
    
    for (const k of keys) {
      translation = translation?.[k];
    }
    
    return translation || key;
  };

  /** Vraća niz iz prevoda (npr. FAQ lista); prazan niz ako ključ ne postoji. */
  const tList = <T,>(key: string): T[] => {
    let node: any = translations[language];
    for (const k of key.split('.')) node = node?.[k];
    return Array.isArray(node) ? (node as T[]) : [];
  };

  // <html lang="..."> prati izabrani jezik (pretraživači i čitači ekrana)
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tList }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
